import React, { useEffect, useMemo, useState } from "react"
import { Modal, Tabs, Input, InputNumber, notification } from "antd"
import Dropzone from "react-dropzone"
import { GoogleMaps, Icons } from "components"
import ZoneDrawMap from "./ZoneDrawMap"
import Strings from "utils/strings"
import {
	safeParseGeojson,
	geojsonToPolygonPaths,
	pathToFeatureCollection,
	LatLng,
} from "utils/geojson"

const { TabPane } = Tabs

export interface ZonePayload {
	name: string
	description?: string
	buffer: number
	geojson: any
}

interface ZoneModalProps {
	visible: boolean
	// The zone being edited; omit (or null) to create a new one.
	zone?: any | null
	onClose: () => void
	// Returns true on success (so the modal can close/reset).
	onSubmit: (payload: ZonePayload) => Promise<boolean>
}

const DEFAULT_BUFFER = 0.1

/**
 * Create or edit a GeojsonZones document, shared by the contract page and the
 * global zone page. Geometry can be pasted, uploaded or drawn; in edit mode the
 * existing geometry is pre-loaded and kept unless the user replaces it.
 */
const ZoneModal: React.FC<ZoneModalProps> = ({
	visible,
	zone = null,
	onClose,
	onSubmit,
}) => {
	const isEdit = Boolean(zone?._id)

	const [activeTab, setActiveTab] = useState("paste")
	const [name, setName] = useState("")
	const [description, setDescription] = useState("")
	const [buffer, setBuffer] = useState<number>(DEFAULT_BUFFER)
	const [pasteText, setPasteText] = useState("")
	const [drawnPath, setDrawnPath] = useState<LatLng[]>([])
	const [submitting, setSubmitting] = useState(false)

	// Load the zone's current values whenever the modal opens. Editing starts on
	// the paste tab with the stored geometry, so an untouched save round-trips it
	// unchanged.
	useEffect(() => {
		if (!visible) return

		setActiveTab("paste")
		setName(zone?.name || "")
		setDescription(zone?.description || "")
		setBuffer(typeof zone?.buffer === "number" ? zone.buffer : DEFAULT_BUFFER)
		setPasteText(zone?.geojson ? JSON.stringify(zone.geojson, null, 2) : "")
		setDrawnPath([])
		setSubmitting(false)
	}, [visible, zone])

	// Resolve the geojson for the active tab (null when not yet valid/complete).
	const resolvedGeojson = useMemo(() => {
		if (activeTab === "paste" || activeTab === "upload") {
			return pasteText.trim() ? safeParseGeojson(pasteText) : null
		}
		if (activeTab === "draw") {
			return drawnPath.length >= 3 ? pathToFeatureCollection(drawnPath) : null
		}
		return null
	}, [activeTab, pasteText, drawnPath])

	const previewPolygons = useMemo(() => {
		const paths = geojsonToPolygonPaths(resolvedGeojson)
		return paths.map((path) => ({
			paths: path,
			fillColor: "#1C70D9",
			fillOpacity: 0.15,
			strokeColor: "#1C70D9",
			strokeOpacity: 1,
			strokeWeight: 2.5,
		}))
	}, [resolvedGeojson])

	const handleFileDrop = (files: File[]) => {
		const file = files?.[0]
		if (!file) return
		const reader = new FileReader()
		reader.onload = () => setPasteText(String(reader.result || ""))
		reader.onerror = () =>
			notification.error({
				message: Strings.contracts.geojsonZones,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
			})
		reader.readAsText(file)
	}

	const handleSubmit = async () => {
		if (!name.trim()) {
			notification.warning({
				message: Strings.contracts.geojsonZones,
				description: Strings.contracts.geojsonNameRequired,
				placement: "bottomRight",
			})
			return
		}
		if (
			!resolvedGeojson ||
			geojsonToPolygonPaths(resolvedGeojson).length === 0
		) {
			notification.warning({
				message: Strings.contracts.geojsonZones,
				description: Strings.contracts.geojsonInvalid,
				placement: "bottomRight",
			})
			return
		}

		setSubmitting(true)
		const ok = await onSubmit({
			name: name.trim(),
			description: description.trim(),
			buffer: buffer ?? 0,
			geojson: resolvedGeojson,
		})
		setSubmitting(false)
		if (ok) onClose()
	}

	return (
		<Modal
			title={
				isEdit
					? Strings.contracts.geojsonEditZone
					: Strings.contracts.geojsonAddZone
			}
			visible={visible}
			onCancel={onClose}
			onOk={handleSubmit}
			okText={
				isEdit ? Strings.contracts.geojsonSave : Strings.contracts.geojsonCreate
			}
			okButtonProps={{ loading: submitting }}
			width={720}
			destroyOnClose
		>
			<div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
				<div style={{ flex: 1 }}>
					<label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
						{Strings.contracts.geojsonZoneName}
					</label>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={Strings.contracts.geojsonZoneName}
					/>
				</div>
				<div style={{ width: 160 }}>
					<label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
						{Strings.contracts.geojsonBuffer}
					</label>
					<InputNumber
						style={{ width: "100%" }}
						min={0}
						step={0.1}
						value={buffer}
						onChange={(v) => setBuffer(typeof v === "number" ? v : 0)}
					/>
				</div>
			</div>

			<div style={{ marginBottom: 16 }}>
				<label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
					{Strings.contracts.geojsonZoneDescription}
				</label>
				<Input
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder={Strings.contracts.geojsonZoneDescription}
				/>
			</div>

			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				destroyInactiveTabPane
			>
				<TabPane tab={Strings.contracts.geojsonPaste} key="paste">
					<Input.TextArea
						rows={8}
						value={pasteText}
						onChange={(e) => setPasteText(e.target.value)}
						placeholder={Strings.contracts.geojsonPastePlaceholder}
					/>
				</TabPane>

				<TabPane tab={Strings.contracts.geojsonUpload} key="upload">
					<Dropzone
						multiple={false}
						accept=".json,.geojson,application/json,application/geo+json"
						onDrop={handleFileDrop}
					>
						<div
							style={{
								border: "1px dashed #ccc",
								borderRadius: 6,
								padding: 24,
								textAlign: "center",
								cursor: "pointer",
							}}
						>
							<Icons.UploadFile />
							<p style={{ marginTop: 8 }}>
								{Strings.contracts.geojsonUploadHint}
							</p>
							{pasteText && (
								<p style={{ color: "#1C70D9", wordBreak: "break-all" }}>
									{pasteText.slice(0, 80)}
									{pasteText.length > 80 ? "…" : ""}
								</p>
							)}
						</div>
					</Dropzone>
				</TabPane>

				<TabPane tab={Strings.contracts.geojsonDraw} key="draw">
					<ZoneDrawMap value={drawnPath} onChange={setDrawnPath} />
				</TabPane>
			</Tabs>

			{previewPolygons.length > 0 && (
				<div style={{ marginTop: 16 }}>
					<label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
						{Strings.contracts.geojsonPreview}
					</label>
					<GoogleMaps
						polygons={previewPolygons}
						mapContainerStyle={{
							width: "100%",
							height: "260px",
							borderRadius: 5,
						}}
					/>
				</div>
			)}
		</Modal>
	)
}

export default ZoneModal
